import React, { useState, useEffect } from "react";
import "./MyOrders.css";
import { supabase } from "../supabase";

// Expandable order list for the profile page. Each card shows the order id,
// date and amount; expanding it reveals the per-line add-on status (ready to
// collect / collected / refunded), which is the point of the section.
//
// There is deliberately NO order-level status chip: the parent order row's
// status is not updated when individual courts are refunded, so any label
// derived from it was guesswork that could contradict the add-on lines below.
// The add-on lines carry their own authoritative state.
//
// Reads run under the user's JWT, scoped by RLS ("Users can view own orders" /
// "Users can view own order add-ons"). Display-only; there are no actions.

// The section leads with orders that still have an upcoming booking. Finished
// orders are kept only as a short greyed-out tail for reference.
const PAST_ORDERS_LIMIT = 5;
// Upper bound on how far back we look for still-active orders. The booking
// window is 10-14 days, so anything active is always well inside this.
const PARENT_FETCH_LIMIT = 40;

// Today's date in Malaysia (the venue's only timezone), as YYYY-MM-DD, to
// compare against bookings.booking_date which is stored as a local date.
const todayInMalaysia = () =>
  new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().split("T")[0];

// Order ids look like "order_ab12cd34_1718000000000" — the middle segment is the
// compact unique part, so prefer it over the trailing timestamp; otherwise fall
// back to the last 8 characters.
const shortOrderId = (orderId) => {
  const parts = String(orderId || "").split("_");
  if (parts.length >= 2 && parts[1]) return parts[1].toUpperCase();
  return String(orderId || "").slice(-8).toUpperCase();
};

const formatOrderDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Per-line add-on status chip (+ optional detail line).
// isPastOrder: the order has no upcoming bookings, so anything never collected
// can no longer be picked up — it reads as expired rather than as an
// outstanding item. Collected/refunded lines keep their real history.
const addOnLineStatus = (line, isPastOrder) => {
  if (line.refunded_at) {
    const bits = [];
    if (line.refund_amount != null && !isNaN(Number(line.refund_amount))) {
      bits.push(`RM ${Number(line.refund_amount).toFixed(2)}`);
    }
    const d = formatOrderDate(line.refunded_at);
    if (d) bits.push(d);
    return { label: "Refunded", tone: "refunded", meta: bits.join(" · ") };
  }
  if (line.redeemed_at) {
    return { label: "Collected", tone: "paid", meta: formatOrderDate(line.redeemed_at) };
  }
  if (isPastOrder) {
    return { label: "Expired", tone: "expired", meta: "Not collected" };
  }
  return { label: "Ready to collect", tone: "pending", meta: "" };
};

// Name from the embedded add_ons row; a soft-deleted/inactive item embeds null,
// so fall back to the matching booking_metadata.add_ons entry, then "Add-on".
const addOnLineName = (line, metadataAddOns) => {
  if (line.add_ons && line.add_ons.name) return line.add_ons.name;
  const meta = (metadataAddOns || []).find((m) => m && m.add_on_id === line.add_on_id);
  return meta?.name || "Add-on";
};

const MyOrders = ({ refreshToken = 0 }) => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  // Receipt resend, per order: which one is in flight and what came back.
  const [resendingOrderId, setResendingOrderId] = useState(null);
  const [resendResults, setResendResults] = useState({});

  const handleResendReceipt = async (orderId) => {
    setResendingOrderId(orderId);
    setResendResults((prev) => ({ ...prev, [orderId]: null }));
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("client-resend-receipt", {
        body: { order_id: orderId },
      });
      if (invokeError) {
        // The real reason lives in the response body, not in invokeError.message.
        let message = "We could not send the receipt. Please try again.";
        try {
          const body = await invokeError.context?.json();
          if (body?.error) message = body.error;
        } catch (parseError) {
          // Keep the generic message.
        }
        setResendResults((prev) => ({ ...prev, [orderId]: { ok: false, message } }));
        return;
      }
      setResendResults((prev) => ({
        ...prev,
        [orderId]: { ok: true, message: data?.message || "Receipt sent." },
      }));
    } catch (err) {
      console.error("MyOrders.js: Error resending receipt:", err);
      setResendResults((prev) => ({
        ...prev,
        [orderId]: { ok: false, message: "We could not send the receipt. Please try again." },
      }));
    } finally {
      setResendingOrderId(null);
    }
  };

  useEffect(() => {
    let isActive = true;

    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          if (isActive) {
            setOrders([]);
            setIsLoading(false);
          }
          return;
        }

        // Parents only, newest first and bounded — the customer's whole order
        // history is never needed here.
        const { data: parentRows, error: queryError } = await supabase
          .from("orders")
          .select(
            "order_id, parent_order_id, total_amount, created_at, booking_metadata, order_add_ons (id, add_on_id, quantity, unit_price, subtotal, redeemed_at, refunded_at, refund_amount, add_ons (name))"
          )
          .eq("user_id", user.id)
          .is("parent_order_id", null)
          .order("created_at", { ascending: false })
          .limit(PARENT_FETCH_LIMIT);

        if (queryError) throw queryError;

        const parentIds = (parentRows || []).map((row) => row.order_id);
        if (parentIds.length === 0) {
          if (isActive) setOrders([]);
          return;
        }

        // Sub-orders locate the family's bookings (bookings attach to
        // sub-orders, not the parent) and carry the credits applied at
        // checkout — the parent's stored total is cash only.
        const { data: subRows, error: subsError } = await supabase
          .from("orders")
          .select("order_id, parent_order_id, booking_metadata")
          .in("parent_order_id", parentIds);
        if (subsError) throw subsError;

        // Promo discounts live in their own table; RLS ("Users can view own
        // promo uses") scopes it. Best-effort: no discount line if it fails.
        const promoByOrderId = {};
        const { data: promoRows, error: promoError } = await supabase
          .from("promo_code_uses")
          .select("order_id, discount_amount")
          .in("order_id", parentIds);
        if (promoError) {
          console.error("MyOrders.js: Error fetching promo uses:", promoError);
        } else {
          for (const row of promoRows || []) {
            promoByOrderId[row.order_id] =
              (promoByOrderId[row.order_id] || 0) + (Number(row.discount_amount) || 0);
          }
        }

        const subsByParent = {};
        for (const row of subRows || []) {
          if (!subsByParent[row.parent_order_id]) subsByParent[row.parent_order_id] = [];
          subsByParent[row.parent_order_id].push(row);
        }
        const parents = (parentRows || []).map((row) => ({
          ...row,
          subOrders: subsByParent[row.order_id] || [],
        }));

        // Which families still have an UPCOMING booking — that plus uncollected
        // add-ons is what makes an order active. Bookings attach to sub-orders
        // (and free slots inside a paid checkout attach to the PARENT), so the
        // whole family is checked. RLS lets the customer read their own rows.
        const familyIds = [];
        for (const parent of parents) {
          familyIds.push(parent.order_id);
          for (const sub of parent.subOrders) familyIds.push(sub.order_id);
        }
        const today = todayInMalaysia();
        const upcomingOrderIds = new Set();
        for (let i = 0; i < familyIds.length; i += 100) {
          const chunk = familyIds.slice(i, i + 100);
          const { data: bookingRows, error: bookingsError } = await supabase
            .from("bookings")
            .select("order_id, booking_date")
            .in("order_id", chunk);
          if (bookingsError) {
            // Fail soft: treat the rest as past rather than hiding the list.
            console.error("MyOrders.js: Error fetching bookings for orders:", bookingsError);
            break;
          }
          for (const b of bookingRows || []) {
            if (b.booking_date && b.booking_date >= today) upcomingOrderIds.add(b.order_id);
          }
        }

        const classified = parents.map((parent) => {
          const familyOrderIds = [parent.order_id, ...parent.subOrders.map((s) => s.order_id)];
          // How the order was actually settled. The parent's total_amount is
          // the CASH charged (checkout subtracts credits before storing it), so
          // a fully credit-paid order stores 0 — which on its own reads as
          // "RM 0.00" and hides that credits were spent. Credits are recorded
          // per sub-order in booking_metadata.credits_to_use.
          const creditsUsed = [parent, ...parent.subOrders].reduce(
            (sum, row) => sum + (Number(row.booking_metadata?.credits_to_use) || 0),
            0
          );
          const cashPaid = Number(parent.total_amount) || 0;
          return {
            ...parent,
            // Active = still has a slot to play. Once every booking is in the
            // past the order is done, and any add-on never collected by then
            // has expired (see addOnLineStatus).
            isActiveOrder: familyOrderIds.some((id) => upcomingOrderIds.has(id)),
            creditsUsed,
            cashPaid,
            promoDiscount: promoByOrderId[parent.order_id] || 0,
            totalPaid: cashPaid + creditsUsed,
          };
        });

        const activeOrders = classified.filter((o) => o.isActiveOrder);
        const pastOrders = classified.filter((o) => !o.isActiveOrder).slice(0, PAST_ORDERS_LIMIT);

        if (isActive) setOrders([...activeOrders, ...pastOrders]);
      } catch (err) {
        console.error("MyOrders.js: Error fetching orders:", err);
        if (isActive) setError("Failed to load your orders. Please try again.");
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchOrders();
    return () => {
      isActive = false;
    };
  }, [refreshToken]);

  const toggleExpanded = (orderId) => {
    setExpandedOrderId((current) => (current === orderId ? null : orderId));
  };

  if (isLoading) {
    return <div className="my-orders-message">Loading orders…</div>;
  }
  if (error) {
    return <div className="my-orders-message my-orders-error">{error}</div>;
  }
  if (orders.length === 0) {
    return <div className="my-orders-message">No orders yet.</div>;
  }

  // Active orders come first; the past tail is labelled so it is clear why a
  // finished order is still listed.
  const firstPastOrderId = orders.find((o) => !o.isActiveOrder)?.order_id;

  return (
    <div className="my-orders-list">
      {orders.map((order) => {
        const isExpanded = expandedOrderId === order.order_id;
        const metadataAddOns = Array.isArray(order.booking_metadata?.add_ons)
          ? order.booking_metadata.add_ons
          : [];
        const addOnLines = order.order_add_ons || [];

        return (
          <React.Fragment key={order.order_id}>
            {order.order_id === firstPastOrderId && (
              <div className="my-orders-subhead">Past orders</div>
            )}
          <div
            className={`order-card ${isExpanded ? "is-expanded" : ""} ${
              order.isActiveOrder ? "" : "is-past"
            }`}
          >
            <button
              type="button"
              className="order-card-header"
              onClick={() => toggleExpanded(order.order_id)}
              aria-expanded={isExpanded}
            >
              <div className="order-card-main">
                <span className="order-card-id">#{shortOrderId(order.order_id)}</span>
                <span className="order-card-date">{formatOrderDate(order.created_at)}</span>
              </div>
              <div className="order-card-side">
                <span className="order-card-total">
                  RM {Number(order.totalPaid || 0).toFixed(2)}
                </span>
                <span className="order-card-chevron" aria-hidden="true" />
              </div>
            </button>

            {isExpanded && (
              <div className="order-card-details">
                <div className="order-payment">
                  {order.promoDiscount > 0 && (
                    <div className="order-payment-row">
                      <span>Promo discount</span>
                      <span>− RM {order.promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {order.creditsUsed > 0 && (
                    <div className="order-payment-row">
                      <span>Paid with credits</span>
                      <span>RM {order.creditsUsed.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="order-payment-row">
                    <span>Paid by card / online banking</span>
                    <span>RM {order.cashPaid.toFixed(2)}</span>
                  </div>
                </div>

                <div className="order-receipt">
                  <button
                    type="button"
                    className="order-receipt-button"
                    onClick={() => handleResendReceipt(order.order_id)}
                    disabled={resendingOrderId === order.order_id}
                  >
                    {resendingOrderId === order.order_id ? "Sending…" : "Resend receipt"}
                  </button>
                  {resendResults[order.order_id] && (
                    <span
                      className={`order-receipt-result ${
                        resendResults[order.order_id].ok ? "ok" : "failed"
                      }`}
                    >
                      {resendResults[order.order_id].message}
                    </span>
                  )}
                </div>

                {addOnLines.length > 0 ? (
                  <ul className="order-addon-list">
                    {addOnLines.map((line) => {
                      const quantity = line.quantity || 1;
                      const lineAmount = Number(line.unit_price || 0) * quantity;
                      const lineStatus = addOnLineStatus(line, !order.isActiveOrder);
                      return (
                        <li key={line.id} className="order-addon-line">
                          <div className="order-addon-info">
                            <span className="order-addon-name">
                              {addOnLineName(line, metadataAddOns)}
                            </span>
                            <span className="order-addon-qty">× {quantity}</span>
                          </div>
                          <div className="order-addon-side">
                            <span className="order-addon-amount">RM {lineAmount.toFixed(2)}</span>
                            <span className={`order-status-chip ${lineStatus.tone}`}>
                              {lineStatus.label}
                            </span>
                          </div>
                          {lineStatus.meta && (
                            <div className="order-addon-meta">{lineStatus.meta}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : metadataAddOns.length > 0 ? (
                  // Legacy pre-June-2026 orders: add-ons only exist in
                  // booking_metadata, with no per-line collection/refund state.
                  <ul className="order-addon-list">
                    {metadataAddOns.map((item, index) => (
                      <li key={`${item?.add_on_id ?? "meta"}-${index}`} className="order-addon-line">
                        <div className="order-addon-info">
                          <span className="order-addon-name">{item?.name || "Add-on"}</span>
                          <span className="order-addon-qty">× {item?.quantity || 1}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="order-no-addons">No add-ons</div>
                )}
              </div>
            )}
          </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MyOrders;
